import type { AbonoJustificativaListItem, FaltaDia, FaltasRestantesItem } from '../../services/mobile-frequencia.service';

export interface FaltasRestantesResultado {
  faltasRestantes: number;
  hasFaltaEducacao: boolean;
}

/**
 * Extrai a mensagem de erro do backend seguindo a ordem de prioridade usada
 * nos catches de `salvar()`/`confirmarExclusaoFinal()`: `error.error.detail.message`
 * > `error.error.message` > `error.message` > mensagem padrão do chamador.
 */
export function extrairMensagemErro(error: any, mensagemPadrao: string): string {
  if (error?.error?.detail?.message) {
    return error.error.detail.message;
  }
  if (error?.error?.message) {
    return error.error.message;
  }
  if (error?.message) {
    return error.message;
  }
  return mensagemPadrao;
}

/**
 * Calcula faltas restantes comparando com dados do endpoint não-justificadas.
 * Detecta se há faltas registradas no sistema Educação.
 * AngularJS: app-faltas-restantes.directive.js → getTotal() + getDescricao()
 *
 * Nota: quando `qtdNaoJustificadas < qtdFaltasSemAbono`, `hasFaltaEducacao` é
 * preservado como veio em `hasFaltaEducacaoAtual` (não é resetado pra false) —
 * comportamento pré-existente, preservado como estava antes da extração.
 */
export function calcularFaltasRestantes(
  data: FaltasRestantesItem[],
  qtdFaltasSemAbono: number,
  hasFaltaEducacaoAtual: boolean
): FaltasRestantesResultado {
  const qtdNaoJustificadas = data.reduce((sum, d) => sum + (d.faltasNaoJustificadas || 0), 0);

  if (qtdNaoJustificadas >= qtdFaltasSemAbono) {
    return {
      faltasRestantes: qtdFaltasSemAbono,
      // hasFaltaEducacao: não há faltas no dropdown MAS existem faltas não justificadas (vindas do Educação)
      hasFaltaEducacao: qtdFaltasSemAbono === 0 && qtdNaoJustificadas > qtdFaltasSemAbono
    };
  }

  // Há mais faltas no dropdown do que o permitido — algumas foram abonadas no Educação
  return { faltasRestantes: qtdNaoJustificadas, hasFaltaEducacao: hasFaltaEducacaoAtual };
}

/**
 * Monta a mensagem descritiva para o banner de faltas restantes.
 * AngularJS: app-faltas-restantes.directive.js → getDescricao()
 */
export function montarMensagemFaltasRestantes(
  faltasRestantes: number,
  qtdFaltasSemAbono: number,
  hasFaltaEducacao: boolean
): string {
  const total = faltasRestantes >= 0 ? faltasRestantes : qtdFaltasSemAbono;

  if (total === 0) {
    let mensagem = 'Não há faltas para Abonar/Justificar!';
    if (hasFaltaEducacao) {
      mensagem +=
        ' Algumas faltas foram registradas nos sistemas Educação ou Professores, indicando apenas o número total de faltas, sem detalhes sobre a data e a aula em que o aluno faltou. Nessas situações, as faltas poderão ser abonadas ou justificadas exclusivamente por meio do sistema Educação.';
    }
    return mensagem;
  }

  if (total === 1) {
    return 'Resta 1 falta a ser Abonada/Justificada.';
  }

  return `Restam ${total} faltas a serem Abonadas/Justificadas.`;
}

/**
 * Formata uma falta individual pra exibição no campo de datas selecionadas.
 * Tenta top-level `data` primeiro; fallback pra `alocacaoAula.data` (falta-aula API).
 *
 * Nota: no modo AULA só usa o formato "dd/mm - Aula N - Matéria" quando a falta
 * tem `alocacaoAula` — senão cai pro formato de data simples. Comportamento
 * ligeiramente diferente de `obterArrayFaltasFormatadas` (que usa esse formato
 * pra qualquer falta em modo AULA, com ou sem `alocacaoAula`) — preservado como
 * estava antes da extração, não são a mesma função.
 */
export function formatarFaltaParaExibicao(falta: FaltaDia, tipoFrequencia: 'DIA' | 'AULA'): string {
  const dataStr = falta.data || falta.alocacaoAula?.data;

  if (!dataStr) {
    // Fallback: mostrar apenas info da aula se disponível
    const numero = falta.alocacaoAula?.numero;
    const materia = falta.itemEducacional?.descricao || '';
    const aulaLabel = numero != null ? `Aula ${numero}` : '';
    const partes = [aulaLabel, materia].filter((p) => p);
    return partes.length > 0 ? partes.join(' - ') : `Falta #${falta.id}`;
  }

  const [ano, mes, dia] = dataStr.split('-');
  const dataFormatada = `${dia}/${mes}`;

  if (tipoFrequencia === 'AULA' && falta.alocacaoAula) {
    const numero = falta.alocacaoAula.numero;
    // itemEducacional está no nível raiz do objeto (não dentro de alocacaoAula)
    const materia = falta.itemEducacional?.descricao || '';
    const aulaLabel = numero != null ? `Aula ${numero}` : '';
    const partes = [dataFormatada, aulaLabel, materia].filter((p) => p);
    return partes.join(' - ');
  }

  return `${dia}/${mes}/${ano}`;
}

/**
 * Monta `faltasParaSelecao`: fora do modo edição usa direto `faltasSemAbono`;
 * em modo edição, mescla as faltas do item sendo editado que não estão mais
 * em `faltasSemAbono` (já vinculadas a este item) e ordena por data.
 */
export function montarFaltasParaSelecao(
  faltasSemAbono: FaltaDia[],
  faltasDoItemEmEdicao: NonNullable<AbonoJustificativaListItem['faltas']>,
  modoEdicao: boolean,
  enturmacaoId: number
): FaltaDia[] {
  if (!modoEdicao || faltasDoItemEmEdicao.length === 0) {
    return faltasSemAbono;
  }

  const faltasComEdicao = [...faltasSemAbono];
  const idsExistentes = new Set(faltasSemAbono.map((f) => f.id));

  for (const falta of faltasDoItemEmEdicao) {
    if (!idsExistentes.has(falta.id)) {
      const dataStr = falta.data || falta.alocacaoAula?.data || '';
      faltasComEdicao.push({
        id: falta.id,
        data: dataStr,
        enturmacao: { id: enturmacaoId },
        tipo: 'FALTA',
        alocacaoAula: falta.alocacaoAula,
        itemEducacional: falta.itemEducacional
      } as FaltaDia);
    }
  }

  return faltasComEdicao.sort((a, b) => {
    const da = a.data || a.alocacaoAula?.data || '';
    const db = b.data || b.alocacaoAula?.data || '';
    return da.localeCompare(db);
  });
}

/**
 * Indica se a flag "todas as faltas selecionadas" deve estar marcada:
 * true quando há faltas disponíveis e a seleção cobre exatamente todas elas.
 */
export function calcularTodasFaltasSelecionadas(faltasParaSelecao: FaltaDia[], faltasSelecionadas: number[]): boolean {
  return faltasParaSelecao.length > 0 && faltasSelecionadas.length === faltasParaSelecao.length;
}

export interface SelecaoMatSelectFaltasResultado {
  faltasSelecionadas: number[];
  todasSelecionadas: boolean;
}

/**
 * Processa o valor bruto do mat-select de faltas (múltipla seleção), que mistura
 * o valor especial `'TODAS'` com ids numéricos reais. Intercepta `'TODAS'` pra
 * marcar/desmarcar tudo; senão filtra só os ids numéricos da seleção individual.
 */
export function processarSelecaoMatSelectFaltas(
  values: (number | string)[],
  todasFaltasSelecionadasAtual: boolean,
  faltasParaSelecao: FaltaDia[]
): SelecaoMatSelectFaltasResultado {
  if (values.includes('TODAS')) {
    if (!todasFaltasSelecionadasAtual) {
      return { faltasSelecionadas: faltasParaSelecao.map((f) => f.id), todasSelecionadas: true };
    }
    return { faltasSelecionadas: [], todasSelecionadas: false };
  }

  const faltasSelecionadas = values.filter((v) => typeof v === 'number') as number[];
  return { faltasSelecionadas, todasSelecionadas: calcularTodasFaltasSelecionadas(faltasParaSelecao, faltasSelecionadas) };
}

/**
 * Monta o texto exibido no campo de datas/aulas selecionadas do mat-select de faltas.
 */
export function obterDatasFormatadas(
  faltasDiasSelecionadas: number[],
  todasFaltasSelecionadas: boolean,
  faltasParaSelecao: FaltaDia[],
  tipoFrequencia: 'DIA' | 'AULA'
): string {
  if (faltasDiasSelecionadas.length === 0) {
    return tipoFrequencia === 'AULA' ? 'Selecione as aulas' : 'Selecione a(s) data(s)';
  }

  if (todasFaltasSelecionadas) {
    return 'Todas as faltas (sem abono e justificativa)';
  }

  const faltasSelecionadas = faltasParaSelecao.filter((f) => faltasDiasSelecionadas.includes(f.id));
  if (faltasSelecionadas.length === 0) return '';

  if (faltasSelecionadas.length > 3) {
    return faltasSelecionadas.slice(0, 3).map((f) => formatarFaltaParaExibicao(f, tipoFrequencia)).join(', ') + '...';
  }

  return faltasSelecionadas.map((f) => formatarFaltaParaExibicao(f, tipoFrequencia)).join(', ');
}

/**
 * Monta o rótulo de data(s) exibido na confirmação de exclusão: prioriza as
 * faltas vinculadas ao item (formatadas), caindo pra data de criação em pt-BR
 * e por fim um traço quando não há nenhuma informação disponível.
 */
export function montarDataExclusaoLabel(
  item: AbonoJustificativaListItem | null,
  tipoFrequencia: 'DIA' | 'AULA'
): string {
  if (!item) {
    return '-';
  }

  const faltasFormatadas = obterArrayFaltasFormatadas(item.faltas, tipoFrequencia);
  if (faltasFormatadas.length > 0) {
    return faltasFormatadas.join(', ');
  }

  if (item.dataCriacao) {
    return new Date(item.dataCriacao).toLocaleDateString('pt-BR');
  }

  return '-';
}

/**
 * Monta o array `faltasDias`/`faltasAulas` do payload de abono/justificativa:
 * resolve cada id selecionado pra sua falta correspondente (lançando se não
 * encontrada) e formata a descrição no padrão dd/mm.
 */
export function montarFaltasSelecionadasPayload(
  faltasDiasSelecionadas: number[],
  faltasParaSelecao: FaltaDia[]
): Array<{ id: number; data: string; descricao: string }> {
  return faltasDiasSelecionadas.map((faltaId) => {
    const falta = faltasParaSelecao.find((f) => f.id === faltaId);
    if (!falta) {
      throw new Error('Falta não encontrada');
    }
    const dataStr = falta.data || falta.alocacaoAula?.data || '';
    return {
      id: falta.id,
      data: dataStr,
      descricao: formatarDataParaDescricao(dataStr)
    };
  });
}

/**
 * Formata data YYYY-MM-DD para DD/MM (ex: "2025-06-23" -> "23/06").
 */
export function formatarDataParaDescricao(data: string): string {
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}`;
}

/**
 * Retorna array de strings formatadas para exibição em badges no histórico.
 * Modo DIA:  "06/11/2024"
 * Modo AULA: "03/02 - Aula 5 - Matemática"
 * (a data fica em alocacaoAula.data e o itemEducacional no nível raiz)
 */
export function obterArrayFaltasFormatadas(
  faltas: AbonoJustificativaListItem['faltas'],
  tipoFrequencia: 'DIA' | 'AULA'
): string[] {
  if (!faltas || faltas.length === 0) return [];

  return faltas.map((f) => {
    const dataStr = f.data || f.alocacaoAula?.data;

    if (!dataStr) {
      const numero = f.alocacaoAula?.numero;
      const materia = f.itemEducacional?.descricao || '';
      const aulaLabel = numero != null ? `Aula ${numero}` : '';
      const partes = [aulaLabel, materia].filter((p) => p);
      return partes.length > 0 ? partes.join(' - ') : `Falta #${f.id}`;
    }

    const [ano, mes, dia] = dataStr.split('-');
    if (tipoFrequencia === 'AULA') {
      const numero = f.alocacaoAula?.numero;
      const materia = f.itemEducacional?.descricao || '';
      const aulaLabel = numero != null ? `Aula ${numero}` : '';
      const partes = [`${dia}/${mes}`, aulaLabel, materia].filter((p) => p);
      return partes.join(' - ');
    }

    return `${dia}/${mes}/${ano}`;
  });
}
